import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import JoditEditor from "jodit-react";
import { ProtectAPi } from '../../../../api/ProtectAPi';
import swal from 'sweetalert';
import BackButton from '../../../tools/BackButton';

const ProductAdd = () => {
    const { http } = ProtectAPi();
    const editor = useRef(null);
    const navigate = useNavigate();

    const [loader, setLoader] = useState('off');
    const [productName, setProductName] = useState('');
    const [productSlug, setProductSlug] = useState('');
    const [layoutDescription, setLayoutDescription] = useState('');
    const [layoutImages, setLayoutImages] = useState([]);
    const [titleError, setTitleError] = useState('');
    const [slugError, setSlugError] = useState('');
    const [descriptionError, setDescriptionError] = useState('');
    const [layoutImageErrors, setLayoutImageErrors] = useState([]);
    const [categoryNames, setCategoryNames] = useState([]);
    const [selectedCategoryName, setSelectedCategoryName] = useState('');

    useEffect(() => {
        http.get('/get-all-product-category')
            .then(res => {
                setCategoryNames(res.data || []);
            })
            .catch(error => console.error('Error fetching categories:', error));
    }, []);

    const handleImageChange = (e) => {
        const files = Array.from(e.target.files);
        const newImages = files.map(file => ({
            file,
            preview: URL.createObjectURL(file),
            alt: ''
        }));
        setLayoutImages(prev => [...prev, ...newImages]);
        setLayoutImageErrors([]);
    };

    const handleAltChange = (index, value) => {
        const updatedImages = [...layoutImages];
        updatedImages[index].alt = value;
        setLayoutImages(updatedImages);
    };

    const handleImageRemove = (index) => {
        const updatedImages = [...layoutImages];
        updatedImages.splice(index, 1);
        setLayoutImages(updatedImages);
    };

    const submitForm = () => {
        setLoader('on');
        const formData = new FormData();
        formData.append('product_name', productName);
        formData.append('slug', productSlug);
        formData.append('description', layoutDescription);
        formData.append('category_name', selectedCategoryName);

        layoutImages.forEach((img, index) => {
            formData.append(`images[${index}]`, img.file);
            formData.append(`alt_texts[${index}]`, img.alt);
        });

        http.post(`/store-product`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        }).then(res => {
            if (res.data.status === '422') {
                const errors = res.data.error;
                setLoader('off');
                setTitleError(errors.product_name ?? '');
                setSlugError(errors.slug ?? '');
                setDescriptionError(errors.description ?? '');
                setLayoutImageErrors(errors.images ?? []);
            } else {
                setLoader('off');
                swal('Success', res.data.msg, 'success');
                navigate('/admin/product-list');
            }
        }).catch(error => {
            console.error('Error:', error);
            setLoader('off');
        });
    };

    const editorConfig = useMemo(() => ({
        readonly: false,
        placeholder: 'Start typing...',
        height: 400,
        uploader: { insertImageAsBase64URI: true }
    }), []);

    return (
        <div className="main-content">
            <section className="section">
                <div className="section-body">
                    <div className="row">
                        <div className="col-12">
                            <div className="card card-primary">
                                <div className="card-body">
                                    <div className="d-flex justify-content-between mb-3">
                                        <h5>Add Product</h5>
                                        <BackButton />
                                    </div>


                                    <div className="form-group mt-4">
                                        <label>Multiple Product Image Upload</label>
                                        <input type="file" className="form-control" multiple onChange={handleImageChange} />
                                        {layoutImageErrors.length > 0 && layoutImageErrors.map((err, i) => (
                                            <span key={i} className="text-danger d-block">{err}</span>
                                        ))}
                                    </div>

                                    {layoutImages.length > 0 && (
                                        <div className="row mt-3">
                                            {layoutImages.map((img, index) => (
                                                <div className="col-md-3 mb-4" key={index}>
                                                    <div className="border p-2 rounded shadow-sm">
                                                        <img src={img.preview} alt="Preview" className="img-fluid mb-2"  style={{ height: '150px', width: '100%' }} />
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            placeholder="Alt text for this image"
                                                            value={img.alt}
                                                            onChange={e => handleAltChange(index, e.target.value)}
                                                            style={{ height: '30px' }}
                                                        />

                                                        <button className="btn btn-danger btn-sm w-100 mt-2" onClick={() => handleImageRemove(index)}>
                                                            Remove
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    <div className="form-group">
                                        <label>Product Name</label>
                                        <input type="text" className="form-control" placeholder='Enter Product Name' value={productName} onChange={e => setProductName(e.target.value)} />
                                        <span className="text-danger">{titleError}</span>
                                    </div>

                                    <div className="form-group">
                                        <label>Slug (Optional)</label>
                                        <input type="text" className="form-control" value={productSlug} placeholder='Enter Slug' onChange={e => setProductSlug(e.target.value)} />
                                        <span className="text-danger">{slugError}</span>
                                    </div>

                                    <div className="form-group">
                                        <label>Select Category</label>
                                        <select className="form-control" value={selectedCategoryName} onChange={e => setSelectedCategoryName(e.target.value)}>
                                            <option value="">Select a category</option>
                                            {categoryNames.map((cat, i) => (
                                                <option key={i} value={cat.id}>{cat.name} ({cat.group_name})</option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="form-group">
                                        <label>Description</label>
                                        <JoditEditor
                                            ref={editor}
                                            value={layoutDescription}
                                            config={editorConfig}
                                            tabIndex={1}
                                            onBlur={newContent => setLayoutDescription(newContent)}
                                            onChange={() => { }}
                                        />
                                        <span className="text-danger">{descriptionError}</span>
                                    </div>

                                    

                                    <div className="mb-2 mt-4">
                                        {loader === 'off' ? (
                                            <button className="btn btn-primary btn-lg" onClick={submitForm}>Create Product</button>
                                        ) : (
                                            <div className="spinner-border text-dark" role="status"></div>
                                        )}
                                    </div>

                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default ProductAdd;
